// Page publique des Membres Fondateurs
// Liste les premiers utilisateurs qui ont rejoint le programme Fondateur

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Crown, Users, Award, Star, MapPin, Briefcase, 
  ChevronRight, Search, Filter, BadgeCheck, ArrowLeft,
  Sparkles, TrendingUp, Clock, Building2
} from 'lucide-react';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import BetaBanner from '../../components/ui/BetaBanner';
import { TrustBadge } from '../../components/badges/TrustBadge';
import { fadeIn, listItem, DURATION, EASE, LIST_STAGGER } from '../../utils/animations';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MembresFondateursPage() {
  const [founders, setFounders] = useState([]);
  const [stats, setStats] = useState({ total_founders: 0, remaining_spots: 1000, percentage_filled: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pro, particulier
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFounders();
    fetchStats();
  }, [filter]);

  const fetchFounders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter !== 'all') {
        params.append('user_type', filter);
      }
      
      const res = await fetch(`${API_URL}/api/founders/list?${params}`);
      const data = await res.json();
      
      if (res.ok) {
        setFounders(data.founders || []);
      }
    } catch (error) {
      console.error('Erreur chargement fondateurs:', error);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/founders/stats`);
      const data = await res.json();
      if (res.ok) {
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const filteredFounders = founders.filter(f => 
    f.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.metier?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <BetaBanner />
      <UnifiedHeader />
      <MainNavigation />

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1E3A5F 0%, #2d5a8a 100%)',
        padding: '80px 20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(16,185,129,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(249,115,22,0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Breadcrumb */}
          <Link 
            to="/pro" 
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 8, 
              color: 'rgba(255,255,255,0.7)', 
              textDecoration: 'none',
              fontSize: 14,
              marginBottom: 24
            }}
          >
            <ArrowLeft size={16} />
            Retour à l'Espace Pro
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: 20,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(16,185,129,0.3)'
            }}>
              <Crown size={40} color="white" />
            </div>
            <div>
              <h1 style={{ 
                margin: 0, 
                fontSize: 'clamp(32px, 5vw, 48px)', 
                fontWeight: 800, 
                color: 'white',
                lineHeight: 1.1
              }}>
                Membres Fondateurs
              </h1>
              <p style={{ margin: '8px 0 0 0', color: 'rgba(255,255,255,0.8)', fontSize: 18 }}>
                Les pionniers d'AbracadaBati
              </p>
            </div>
          </div>

          <p style={{ 
            color: 'rgba(255,255,255,0.9)', 
            fontSize: 16, 
            maxWidth: 700,
            lineHeight: 1.7,
            marginBottom: 32
          }}>
            Ils ont cru en notre vision dès le départ. Découvre les artisans et professionnels 
            qui ont rejoint l'aventure AbracadaBati en tant que <strong>Membres Fondateurs</strong>. 
            Leur confiance nous honore et nous motive à construire la meilleure plateforme pour le bâtiment.
          </p>

          {/* Stats Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 16,
            maxWidth: 800
          }}>
            <StatCard 
              icon={Users} 
              value={stats.total_founders} 
              label="Fondateurs inscrits" 
              color="#10b981"
            />
            <StatCard 
              icon={Award} 
              value={stats.remaining_spots} 
              label="Places restantes" 
              color="#F97316"
            />
            <StatCard 
              icon={TrendingUp} 
              value={`${stats.percentage_filled}%`} 
              label="Objectif atteint" 
              color="#8b5cf6"
            />
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        padding: '24px 20px'
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Sparkles size={24} color="white" />
            <span style={{ color: 'white', fontWeight: 600, fontSize: 16 }}>
              Rejoins les fondateurs — 1 an d'abonnement Pro offert !
            </span>
          </div>
          <Link 
            to="/pro/fondateur"
            style={{
              background: 'white',
              color: '#059669',
              padding: '12px 24px',
              borderRadius: 50,
              fontWeight: 700,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            Devenir Fondateur
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      {/* Filters & Search */}
      <section style={{ padding: '32px 20px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ 
            display: 'flex', 
            gap: 16, 
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 24
          }}>
            {/* Search */}
            <div style={{ 
              flex: 1, 
              minWidth: 250,
              position: 'relative' 
            }}>
              <Search 
                size={18} 
                style={{ 
                  position: 'absolute', 
                  left: 16, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#94a3b8'
                }} 
              />
              <input
                type="text"
                placeholder="Rechercher un fondateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 14px 14px 48px',
                  borderRadius: 12,
                  border: '1px solid #e2e8f0',
                  fontSize: 15,
                  outline: 'none',
                  background: 'white'
                }}
              />
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { id: 'all', label: 'Tous' },
                { id: 'pro', label: 'Pros' },
                { id: 'particulier', label: 'Particuliers' }
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  style={{
                    padding: '12px 20px',
                    borderRadius: 10,
                    border: 'none',
                    background: filter === f.id ? '#1E3A5F' : 'white',
                    color: filter === f.id ? 'white' : '#64748b',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                    boxShadow: filter === f.id ? '0 4px 12px rgba(30,58,95,0.2)' : 'none'
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founders Grid */}
      <section style={{ padding: '0 20px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              Chargement des fondateurs...
            </div>
          ) : filteredFounders.length === 0 ? (
            <motion.div
              {...fadeIn(0)}
              style={{
                textAlign: 'center',
                padding: 80,
                background: 'white',
                borderRadius: 24,
                border: '2px dashed #e2e8f0'
              }}
            >
              <Crown size={64} style={{ color: '#cbd5e1', marginBottom: 24 }} />
              <h3 style={{ margin: '0 0 12px 0', color: '#64748b', fontWeight: 700, fontSize: 24 }}>
                Sois le premier Fondateur !
              </h3>
              <p style={{ margin: '0 0 24px 0', color: '#94a3b8', fontSize: 16 }}>
                Aucun membre fondateur pour le moment. Rejoins-nous et inscris ton nom dans l'histoire d'AbracadaBati.
              </p>
              <Link 
                to="/pro/fondateur"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: 50,
                  fontWeight: 700,
                  textDecoration: 'none',
                  fontSize: 16
                }}
              >
                <Crown size={20} />
                Devenir Membre Fondateur
              </Link>
            </motion.div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
              gap: 20 
            }}>
              {filteredFounders.map((founder, index) => (
                <FounderCard key={founder.id || index} founder={founder} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      
    </div>
  );
}

// Composant StatCard
function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.1)',
      backdropFilter: 'blur(10px)',
      borderRadius: 16,
      padding: 20,
      border: '1px solid rgba(255,255,255,0.2)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: `${color}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon size={22} color={color} />
        </div>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, color: 'white' }}>
            {value}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
            {label}
          </div>
        </div>
      </div>
    </div>
  );
}

// Composant FounderCard
function FounderCard({ founder, index }) {
  const isPro = founder.user_type === 'pro' || founder.user_type === 'artisan';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * LIST_STAGGER, duration: DURATION, ease: EASE }}
      style={{ willChange: 'transform, opacity' }}
    >
      <Link 
        to={`/reseau/profil/${founder.username}`}
        style={{ textDecoration: 'none' }}
      >
        <div style={{
          background: 'white',
          borderRadius: 20,
          padding: 24,
          border: '1px solid #e2e8f0',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        >
          {/* Founder Number Badge */}
          {founder.founder_number && (
            <div style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '4px 10px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700
            }}>
              #{founder.founder_number}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            {/* Avatar */}
            {founder.profile_photo ? (
              <img 
                src={founder.profile_photo} 
                alt={founder.display_name}
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid #10b981'
                }}
              />
            ) : (
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1E3A5F 0%, #2d5a8a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700,
                fontSize: 24,
                border: '3px solid #10b981'
              }}>
                {founder.display_name?.charAt(0)?.toUpperCase() || 'F'}
              </div>
            )}

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1E3A5F' }}>
                  {founder.display_name || founder.username}
                </h3>
                {founder.is_verified && (
                  <BadgeCheck size={18} style={{ color: '#3b82f6', fill: '#3b82f6' }} />
                )}
                {founder.trust_level && (
                  <TrustBadge trustLevel={founder.trust_level} size="sm" showLabel={false} />
                )}
              </div>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#64748b' }}>
                @{founder.username}
              </p>
            </div>
          </div>

          {/* Badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {/* Badge Fondateur */}
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '6px 12px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600
            }}>
              <Crown size={14} />
              Fondateur
            </span>

            {/* Badge PRO */}
            {isPro && (
              <span style={{
                background: '#1E3A5F',
                color: 'white',
                padding: '6px 12px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600
              }}>
                PRO
              </span>
            )}
          </div>

          {/* Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {founder.metier && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#64748b' }}>
                <Briefcase size={16} />
                {founder.metier}
              </div>
            )}
            {founder.company_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#64748b' }}>
                <Building2 size={16} />
                {founder.company_name}
              </div>
            )}
            {founder.city && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#64748b' }}>
                <MapPin size={16} />
                {founder.city}
              </div>
            )}
          </div>

          {/* CTA */}
          <div style={{ 
            marginTop: 16, 
            paddingTop: 16, 
            borderTop: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>
              Voir le profil
            </span>
            <ChevronRight size={18} style={{ color: '#94a3b8' }} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
